import React, { useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { CloseIcon, ImageIcon, CheckIcon } from './Icons';

// Built-in chat backgrounds. Keys must match CHAT_WALLPAPER_PRESETS in
// server/index.js and the .wa-wallpaper-* classes in App.css.
export const WALLPAPER_PRESETS = [
  { id: 'doodle', label: 'Doodle' },
  { id: 'plain', label: 'Plain' },
  { id: 'grain', label: 'Texture' },
  { id: 'mint', label: 'Mint' },
  { id: 'lavender', label: 'Lavender' },
  { id: 'sand', label: 'Sand' },
  { id: 'ocean', label: 'Ocean' },
];

// The class + inline style to put on .whatsapp-chat-thread for a user's
// saved wallpaper. No saved choice (null) = the default doodle.
export const wallpaperProps = (wallpaper) => {
  if (wallpaper?.preset === 'image' && wallpaper.imageUrl) {
    return {
      className: 'wa-wallpaper-image',
      style: { '--wa-wallpaper-image': `url("${wallpaper.imageUrl}")` },
    };
  }
  const preset = WALLPAPER_PRESETS.some((p) => p.id === wallpaper?.preset) ? wallpaper.preset : 'doodle';
  return { className: `wa-wallpaper-${preset}`, style: undefined };
};

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

// Lets the logged-in agent pick their own WhatsApp chat background — a
// preset or their own photo. Saved on their account (see
// PUT/POST /api/auth/me/chat-wallpaper), so it follows them to any computer
// and never changes what other agents see.
function WhatsAppWallpaperPicker({ onClose }) {
  const { authFetch, user, updateUser } = useAuth();
  const current = user?.chatWallpaper || null;
  const currentId = current?.preset || 'doodle';
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const applyResponse = async (response) => {
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to save wallpaper');
    updateUser({ chatWallpaper: data.chatWallpaper });
  };

  const choosePreset = async (preset) => {
    if (saving || preset === currentId) return;
    setSaving(true);
    setError('');
    try {
      await applyResponse(
        await authFetch('/api/auth/me/chat-wallpaper', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preset }),
        })
      );
    } catch (err) {
      setError(err.message || 'Failed to save wallpaper');
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Pick an image file (JPG, PNG, WEBP or GIF).');
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('That image is over 5 MB — pick a smaller one.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('wallpaper', file);
      await applyResponse(await authFetch('/api/auth/me/chat-wallpaper/image', { method: 'POST', body: formData }));
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay modal-overlay-center" onClick={onClose}>
      <div className="modal-panel modal-panel-narrow wa-wallpaper-modal" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Chat wallpaper</h2>
            <p className="modal-subtitle">Only you see this. It's saved to your account.</p>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <CloseIcon width={18} height={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="wa-wallpaper-grid" role="radiogroup" aria-label="Chat wallpaper">
            {WALLPAPER_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                role="radio"
                aria-checked={currentId === preset.id}
                className={`wa-wallpaper-option ${currentId === preset.id ? 'wa-wallpaper-option-active' : ''}`}
                onClick={() => choosePreset(preset.id)}
                disabled={saving}
              >
                <span className={`whatsapp-chat-thread wa-wallpaper-swatch wa-wallpaper-${preset.id}`}>
                  <span className="wa-wallpaper-swatch-bubble wa-wallpaper-swatch-in" />
                  <span className="wa-wallpaper-swatch-bubble wa-wallpaper-swatch-out" />
                  {currentId === preset.id && (
                    <span className="wa-wallpaper-check">
                      <CheckIcon width={13} height={13} />
                    </span>
                  )}
                </span>
                <span className="wa-wallpaper-label">
                  {preset.label}
                  {preset.id === 'doodle' && <span className="wa-wallpaper-default"> · default</span>}
                </span>
              </button>
            ))}

            <button
              type="button"
              role="radio"
              aria-checked={currentId === 'image'}
              className={`wa-wallpaper-option ${currentId === 'image' ? 'wa-wallpaper-option-active' : ''}`}
              onClick={() => fileInputRef.current?.click()}
              disabled={saving}
            >
              {currentId === 'image' && current?.imageUrl ? (
                <span
                  className="whatsapp-chat-thread wa-wallpaper-swatch wa-wallpaper-image"
                  style={{ '--wa-wallpaper-image': `url("${current.imageUrl}")` }}
                >
                  <span className="wa-wallpaper-check">
                    <CheckIcon width={13} height={13} />
                  </span>
                </span>
              ) : (
                <span className="wa-wallpaper-swatch wa-wallpaper-upload">
                  <ImageIcon width={22} height={22} />
                </span>
              )}
              <span className="wa-wallpaper-label">{currentId === 'image' ? 'Change photo' : 'Your photo'}</span>
            </button>
          </div>
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleFile} hidden />
          {saving && <p className="wa-info-muted">Saving…</p>}
          {error && <div className="call-feedback call-feedback-error">{error}</div>}
        </div>
      </div>
    </div>
  );
}

export default WhatsAppWallpaperPicker;
